/**
 * As trilhas sincronizadas de uma vertical (spec 07 §6.1, item 4; onda 313, R4, R7, R8, R9, R10).
 *
 * O eixo de profundidade cresce para baixo (ou a cota, boca − z); cada trilha é uma ou mais séries do
 * `Resultado`, desenhadas pela dizimação por linha de pixel (`dizimarPorPixel`: o pico de uma leitura
 * fica). O cursor (o laranja do «você está aqui») é um só para todas as trilhas, pelo ponteiro e pelas
 * setas (↑/↓ uma leitura; Shift estende a seleção; Esc limpa a seleção); a seleção de trecho é um arrasto
 * vertical em qualquer trilha. O cursor, as setas e a seleção andam nas leituras de verdade (todas), nunca
 * nos pontos do traço.
 *
 * Por cima das séries: os destacados (as leituras que o ábaco selecionou, como marcas no eixo), o NA e o
 * u₀ na cor da água, as dissipações como marca com rótulo (link), as camadas como linhas e uma coluna de
 * unidades, as sugestões tracejadas e, numa simulação, o antes tracejado no cinza (cada série do antes no
 * z dele). O título de cada trilha leva a marca de homologação (R7); o Portal passa
 * `mostrarNaoHomologadas` falso e a série não homologada não aparece.
 *
 * O SVG é desenhado em pixel, na largura medida (`useLargura`). Com pouca largura (o celular), as trilhas
 * quebram em linhas, cada uma com o seu eixo, com 120 px no mínimo cada. A altura da trilha vem de quem
 * desenha (`altura`, a da janela no LabManager); sem ela, 520 px (360 px no celular).
 *
 * Onda 347 (R13, o zoom de profundidade; `zoomDeProfundidade.tsx`): com `aoZoom`, arrastar para baixo seleciona (como
 * hoje) e dá o zoom no trecho — o eixo de todas as trilhas passa a ser só ele, e no automático a escala horizontal sai das
 * leituras do trecho —; arrastar para cima volta à vertical inteira e limpa a seleção; o movimento curto (< 8 px) é o
 * clique; no celular, o toque longo começa o gesto (sem ele, o dedo rola a página). O que cai fora do trecho não se
 * desenha.
 *
 * Onda 315 (Tarefa 315.5b; os pedidos do Humberto de 12/10/2026, vendo o CPeT-IT):
 *  · `colorirPorZona` pinta a área entre o eixo e a curva das trilhas com `preencherPorZona` pela zona de cada
 *    trecho — o qt pela zona SBT (Robertson, 2010), o Qtn pela SBTn (Robertson, 2009) —, a mesma regra do PDF
 *    (`cptu-graficos.ts`): só com a coluna da zona homologada com as premissas (`marcaDaColuna`); sem ela, a trilha
 *    fica sem cor e uma nota diz por quê. A legenda escreve a classificação e as zonas presentes, número e nome;
 *  · `fundoDeIc` desenha, ao fundo da trilha do Ic, as faixas das zonas SBTn pelas fronteiras de Ic (1,31 · 2,05 ·
 *    2,60 · 2,95 · 3,60 — as do CPeT-IT, pedido do Humberto), cada uma com a cor e o nome na legenda;
 *  · `linha` traça uma referência vertical rotulada (o limite de Qtn,cs das premissas — sem a faixa contrátil ×
 *    dilatante, que não tem fonte);
 *  · as dissipações com os valores (`inicio`, `fim`, `ui`) vão à trilha de u₂ NA ESCALA DE PRESSÃO, como no PDF:
 *    ● o início (o primeiro u lido), ▼ o fim (o u₀ adotado; «ext.» quando o t₅₀ sai extrapolado da reta) e ◆ o uᵢ
 *    em t = 0 (da reta), com a legenda escrita; o link para a dissipação aberta continua. Sem os valores, a marca
 *    na borda, como antes.
 */
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';
import { GRANDEZAS, ZONAS_SBTN, ZONAS_SBTN_MOD } from '../motor';
import type { ColunaDoMotor } from '../motor';
import type { Grandeza, Resultado } from '../motor';
import { dizimarPorPixel } from './dizimar';
import { ControleDaEscala } from './ControleDaEscala';
import { dominioDaEscala, limitesFixos, marcasDaEscala } from './escalaDoGrafico';
import type { EscalaDoGrafico } from './escalaDoGrafico';
import { dominioDe, escala, faixaBr, marcas, numeroBr } from './escalas';
import type { Escala, EscalaDoEixo } from './escalas';
import { MARCA_NAO_HOMOLOGADA, marcaDaColuna } from './homologacao';
import { indiceNaProfundidade } from './leitura';
import { LegendaSbt } from './LegendaSbt';
import { useLargura } from './useLargura';
import { ARRASTO_DO_ZOOM, BarraDoZoom, useArrastoVertical, zoomRecortado } from './zoomDeProfundidade';
import type { TrechoDoZoom } from './zoomDeProfundidade';

/** Uma trilha: o título, a unidade, a escala, as séries (cada uma com a classe do traço) e a faixa de zonas. */
export interface DefinicaoDaTrilha {
  chave: string;
  titulo: string;
  unidade: string;
  escala: EscalaDoEixo;
  series: readonly { coluna: Grandeza; classe: string }[];
  /** Uma faixa com a cor da zona de cada leitura, junto da borda direita da trilha. */
  faixaDeZonas?: 'sbtn' | 'sbtnMod';
  /** O domínio fixo do eixo (sem ele, o dos dados, com o zero no linear). */
  dominio?: [number, number];
  /**
   * A área entre o eixo e a curva pintada pela zona de cada trecho, com `colorirPorZona` (onda 315): o qt pela zona
   * SBT, o Qtn pela SBTn — só com a coluna da zona homologada.
   */
  preencherPorZona?: 'sbt' | 'sbtn';
  /** As faixas das zonas SBTn pelas fronteiras de Ic ao fundo da trilha (a trilha do Ic; onda 315). */
  fundoDeIc?: boolean;
  /** Uma linha vertical de referência, com o rótulo no desenho e a nota embaixo (onda 315). */
  linha?: { valor: number; rotulo: string; nota: string };
}

/** O que a legenda diz da cor de uma trilha pintada pela zona (a mesma frase do PDF, `cptu-graficos.ts`). */
export const CLASSIFICACAO_DA_COR: Readonly<Record<'sbt' | 'sbtn', string>> = Object.freeze({
  sbt: 'cor: zona SBT (Robertson, 2010)',
  sbtn: 'cor: zona SBTn (Robertson, 2009)',
});

/**
 * As faixas das zonas SBTn pelo Ic, de baixo para cima: as fronteiras 1,31 · 2,05 · 2,60 · 2,95 · 3,60 (as do CPeT-IT,
 * pedido do Humberto em 12/10/2026). É o fundo da trilha, não a classificação: a zona de cada leitura é a do motor
 * (as fronteiras do ábaco), que a faixa SBTn mostra.
 */
export const FAIXAS_DE_IC: readonly { de: number; ate: number; zona: number }[] = Object.freeze([
  { de: 0, ate: 1.31, zona: 7 }, { de: 1.31, ate: 2.05, zona: 6 }, { de: 2.05, ate: 2.6, zona: 5 },
  { de: 2.6, ate: 2.95, zona: 4 }, { de: 2.95, ate: 3.6, zona: 3 }, { de: 3.6, ate: Infinity, zona: 2 },
]);

/** Uma dissipação na trilha de u₂: a profundidade, o rótulo e o link; com os valores (kPa), as três marcas. */
export interface DissipacaoNaTrilha {
  z: number;
  rotulo: string;
  para?: string;
  /** O primeiro u lido (●). */
  inicio?: number;
  /** O u₀ adotado (▼). */
  fim?: number;
  /** O uᵢ em t = 0, da reta (◆). */
  ui?: number;
  /** O t₅₀ sai extrapolado da reta (a curva não chegou a u₅₀): «ext.» junto do ▼. */
  extrapolado?: boolean;
}

/** As trilhas com que a tela começa: qt (com qc), fs, u₂ (com u₀), Rf e Ic com a faixa SBTn. */
export const TRILHAS_PADRAO: readonly DefinicaoDaTrilha[] = Object.freeze([
  { chave: 'qt', titulo: 'qt', unidade: 'MPa', escala: 'linear', series: [{ coluna: 'qc', classe: 'serie-qc' }, { coluna: 'qt', classe: 'serie-qt' }], preencherPorZona: 'sbt' },
  { chave: 'fs', titulo: 'fs', unidade: 'kPa', escala: 'linear', series: [{ coluna: 'fs', classe: 'serie-fs' }] },
  { chave: 'u2', titulo: 'u₂', unidade: 'kPa', escala: 'linear', series: [{ coluna: 'u2', classe: 'serie-u2' }, { coluna: 'u0', classe: 'serie-u0' }] },
  { chave: 'rf', titulo: 'Rf', unidade: '%', escala: 'linear', series: [{ coluna: 'rf', classe: 'serie-rf' }] },
  { chave: 'ic', titulo: 'Ic', unidade: '', escala: 'linear', series: [{ coluna: 'ic', classe: 'serie-ic' }], faixaDeZonas: 'sbtn', dominio: [1, 4] },
] as DefinicaoDaTrilha[]);

/** As medidas do desenho, em pixel. */
const LARGURA_MINIMA = 120;
const EIXO = 48;
/**
 * A coluna das unidades: 72 px no computador (a 1.440 px as cinco trilhas e ela cabem numa linha) e 52 no celular. O
 * nome quebra em linhas dentro dela; o que ainda não cabe (palavra maior que a coluna, ou a camada fina demais para as
 * linhas) vai escrito na lista embaixo das trilhas — o `title` nunca é o único caminho (P-UI-44).
 */
const UNIDADES = 72;
const UNIDADES_ESTREITA = 52;
/** A largura de uma letra de 12 px (uma estimativa folgada) e a altura de uma linha do nome. */
const LETRA = 7;
const LINHA_DO_NOME = 14;
const CABECALHO = 72;
const RODAPE = 8;
const FAIXA = 14;
/** O arrasto mínimo da seleção sem o zoom (o de hoje, 313); com o zoom, o `ARRASTO_DO_ZOOM`. */
const ARRASTO_MINIMO = 4;

/** Os valores de `v` cujas leituras estão no trecho [de, até] do eixo (o domínio automático do trecho visível). */
function valoresNoTrecho(z: ArrayLike<number>, v: ArrayLike<number>, t: readonly [number, number]): number[] {
  const saida: number[] = [];
  const n = Math.min(z.length, v.length);
  for (let i = 0; i < n; i++) if (z[i] >= t[0] - 1e-9 && z[i] <= t[1] + 1e-9) saida.push(v[i]);
  return saida;
}

/**
 * Uma série pronta para o desenho: o `d` do path. Com os limites fixos (`prender`), o valor de fora vai à borda
 * [esc.corte]. O valor que existe mas não tem lugar no eixo (≤ 0 no log) não é desenhado e abre uma falha na curva;
 * a leitura sem valor (NaN), como hoje, não quebra o traço.
 */
function caminho(
  z: ArrayLike<number>, v: ArrayLike<number>, quebras: readonly number[], ex: Escala, ez: Escala,
  prender?: { min: number; max: number } | null,
): string {
  const partes: string[] = [];
  let q = 0;
  let falha = false;
  for (let k = 0; k < z.length; k++) {
    const nova = k === 0 || falha || (q < quebras.length && quebras[q] === k);
    if (q < quebras.length && quebras[q] === k) q++;
    const y = ez.para(z[k]);
    const semLugar = Number.isFinite(v[k]) && !Number.isFinite(ex.para(v[k])); // ≤ 0 no log
    const x = prender && !semLugar && Number.isFinite(v[k]) ? ex.para(Math.min(prender.max, Math.max(prender.min, v[k]))) : ex.para(v[k]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      if (semLugar && Number.isFinite(y)) falha = true;
      continue;
    }
    falha = false;
    partes.push(`${nova ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`);
  }
  return partes.join('');
}

/**
 * O nome da unidade quebrado em linhas de até `caracteres` letras, pelas palavras; null quando uma palavra sozinha não
 * cabe na linha (o nome vai inteiro para a lista).
 */
function quebrarONome(nome: string, caracteres: number): string[] | null {
  const linhas: string[] = [];
  for (const palavra of nome.trim().split(/\s+/).filter(Boolean)) {
    if (palavra.length > caracteres) return null;
    const ultima = linhas[linhas.length - 1];
    if (ultima !== undefined && ultima.length + 1 + palavra.length <= caracteres) linhas[linhas.length - 1] = `${ultima} ${palavra}`;
    else linhas.push(palavra);
  }
  return linhas;
}

/** As casas do rótulo de uma marca: as que o passo pede, e nenhuma a mais. */
function rotuloDaMarca(v: number, todas: readonly number[], tipo: EscalaDoEixo): string {
  if (tipo === 'log') return numeroBr(v, v >= 1 ? 0 : Math.ceil(-Math.log10(v) - 1e-9));
  const passo = todas.length > 1 ? Math.abs(todas[1] - todas[0]) : 1;
  // As casas do passo (o digitado pode ser 2,5: «0 · 2,5 · 5»); nas marcas de 1, 2 ou 5 × 10ⁿ, as de sempre.
  const doPasso = Math.min(6, (String(Number(passo.toPrecision(12))).split('.')[1] ?? '').length);
  return numeroBr(v, Math.max(0, Math.ceil(-Math.log10(passo) - 1e-9), doPasso));
}

/**
 * As marcas de um eixo que cabem na largura: as de `marcas`, e, se os rótulos se encostam, uma sim e outra
 * não (e assim por diante) — o rótulo nunca passa por cima do vizinho.
 */
function marcasQueCabem(tipo: EscalaDoEixo, dominio: [number, number], ex: Escala, e?: EscalaDoGrafico | null): number[] {
  const todas = e ? marcasDaEscala(e, dominio, 6) : marcas(tipo, dominio, 6);
  const largo = Math.max(...todas.map((v) => rotuloDaMarca(v, todas, tipo).length)) * 7 + 10;
  for (let pulo = 1; pulo < todas.length; pulo++) {
    const escolhidas = todas.filter((_, k) => k % pulo === 0);
    let cabe = true;
    for (let k = 1; k < escolhidas.length && cabe; k++) cabe = Math.abs(ex.para(escolhidas[k]) - ex.para(escolhidas[k - 1])) >= largo;
    if (cabe) return escolhidas;
  }
  return todas.slice(0, 1);
}

/** O fundo de cada trilha: a profundidade e a escala horizontal dela. */
interface Coluna {
  def: DefinicaoDaTrilha;
  x0: number;
  largura: number;
  ex: Escala;
  dominioX: [number, number];
  /** O tipo do eixo: o da escala escolhida, ou o da definição. */
  tipo: EscalaDoEixo;
  /** A escala escolhida para a trilha (onda 347); null: a de hoje. */
  ajuste: EscalaDoGrafico | null;
  /** Os limites fixos da escala escolhida (o corte na borda, com «▸»); null no automático. */
  fixos: { min: number; max: number } | null;
  series: { coluna: Grandeza; classe: string }[];
  homologada: boolean;
  motivo: string;
  /** A zona que pinta a área (com `colorirPorZona` e a coluna da zona homologada); null: sem cor. */
  pinta: 'sbt' | 'sbtn' | null;
}

/** As marcas de uma série de zonas: um retângulo por trecho de leituras seguidas da mesma zona. */
function trechosDeZona(zonas: Int8Array, n: number, f: (i: number, j: number, zona: number) => void): void {
  let i = 0;
  while (i < n) {
    let j = i;
    while (j + 1 < n && zonas[j + 1] === zonas[i]) j++;
    f(i, j, zonas[i]);
    i = j + 1;
  }
}

/**
 * O preenchimento pela zona (o molde do `poligonosPorZona` do PDF): um polígono por trecho de leituras seguidas da
 * mesma zona, entre o eixo (`xBase`) e a curva, com as leituras do trecho dizimadas pela linha de pixel; o trecho vai
 * de meio caminho da leitura anterior a meio caminho da seguinte, como a faixa. Zona 0 fica sem cor.
 */
function poligonosPorZona(x: {
  r: Resultado; coluna: Grandeza; zonas: Int8Array; ex: Escala; ez: Escala; xBase: number; dominio: [number, number];
  tipo: EscalaDoEixo; janela: { z0: number; z1: number; altura: number }; rotulo: string;
}): { d: string; token: string; texto: string }[] {
  const z = x.r.col.z;
  const v = x.r.col[x.coluna];
  const n = Math.min(x.r.n, z.length, x.zonas.length);
  const zonasDoMotor = ZONAS_SBTN as Readonly<Record<number, { token: string; nome: string }>>;
  const [d0, d1] = x.dominio;
  const naBorda = (valor: number): number => {
    if (!Number.isFinite(valor)) return NaN;
    if (x.tipo === 'log' && valor <= 0) return d0;
    return Math.min(d1, Math.max(d0, valor));
  };
  const saida: { d: string; token: string; texto: string }[] = [];
  trechosDeZona(x.zonas, n, (i, j, zona) => {
    const desc = zonasDoMotor[zona];
    if (!desc) return;
    const de = i === 0 ? z[i] : (z[i - 1] + z[i]) / 2;
    const ate = j + 1 < n ? (z[j] + z[j + 1]) / 2 : z[j];
    const zs: number[] = [];
    const vs: number[] = [];
    for (let k = i; k <= j; k++) { zs.push(z[k]); vs.push(naBorda(v[k])); }
    const dz = dizimarPorPixel(zs, vs, x.janela);
    const pts: string[] = [];
    let primeiro = NaN;
    let ultimo = NaN;
    for (let k = 0; k < dz.z.length; k++) {
      const px = x.ex.para(dz.v[k]);
      const py = x.ez.para(dz.z[k]);
      if (!Number.isFinite(px) || !Number.isFinite(py)) continue;
      if (!Number.isFinite(primeiro)) primeiro = px;
      ultimo = px;
      pts.push(`L${px.toFixed(1)} ${py.toFixed(1)}`);
    }
    const ya = x.ez.para(de);
    const yb = x.ez.para(ate);
    if (pts.length === 0 || !Number.isFinite(ya) || !Number.isFinite(yb)) return;
    saida.push({
      d: `M${x.xBase.toFixed(1)} ${ya.toFixed(1)}L${primeiro.toFixed(1)} ${ya.toFixed(1)}${pts.join('')}`
        + `L${ultimo.toFixed(1)} ${yb.toFixed(1)}L${x.xBase.toFixed(1)} ${yb.toFixed(1)}z`,
      token: desc.token,
      texto: `${x.rotulo} ${zona} · ${desc.nome}`,
    });
  });
  return saida;
}

/** As trilhas de uma vertical. */
export function Trilhas(p: {
  r: Resultado;
  antes?: Resultado | null;
  trilhas: readonly DefinicaoDaTrilha[];
  eixo: 'profundidade' | 'cota';
  cota: number | null;
  cursor: number | null;
  aoCursor(z: number | null): void;
  selecao: { de: number; ate: number } | null;
  aoSelecionar(s: { de: number; ate: number } | null): void;
  destacados?: Uint8Array | null;
  na?: number | null;
  dissipacoes?: readonly DissipacaoNaTrilha[];
  /**
   * Pinta as trilhas com `preencherPorZona` pela zona (onda 315): o LabManager liga e desliga na tela; sem ela, não
   * pinta (o Portal decide quando quiser).
   */
  colorirPorZona?: boolean;
  camadas?: readonly { topo: number; base: number; unidade: string }[];
  sugestoes?: readonly number[];
  mostrarNaoHomologadas?: boolean;
  /** Abre o `para` de uma dissipação sem recarregar a página (o `navigate` do roteador da tela); sem ele, o link. */
  aoNavegar?(para: string): void;
  /** A altura de cada trilha, em pixel (a área do desenho, sem o cabeçalho); sem ela, 520 (360 no celular). */
  altura?: number;
  /**
   * A escala horizontal escolhida, por chave da trilha (onda 347, R12); sem ela, a de hoje. Com `aoMudarEscala`, cada
   * trilha ganha o botão «Escala» no título.
   */
  escalas?: Readonly<Record<string, EscalaDoGrafico>>;
  aoMudarEscala?(grandeza: string, e: EscalaDoGrafico | null): void;
  /**
   * O zoom de profundidade (onda 347, R13): o trecho do eixo, em m; null, a vertical inteira. Com `aoZoom`, arrastar
   * para baixo seleciona (como hoje) e dá o zoom, arrastar para cima volta à vertical inteira e limpa a seleção, o toque
   * longo começa o gesto no celular, e a barra «Profundidade de [ ] a [ ] m» entra no cabeçalho. Sem `aoZoom`, o gesto
   * de hoje (só a seleção).
   */
  zoom?: TrechoDoZoom | null;
  aoZoom?(z: TrechoDoZoom | null): void;
}): JSX.Element {
  const { r, antes } = p;
  const [ref, largura] = useLargura<HTMLDivElement>();
  const mostrarTudo = p.mostrarNaoHomologadas !== false;
  const porCota = p.eixo === 'cota' && p.cota !== null && Number.isFinite(p.cota);
  const altura = p.altura !== undefined && Number.isFinite(p.altura) && p.altura >= 100
    ? Math.round(p.altura) : largura > 0 && largura < 640 ? 360 : 520;

  // O fim das leituras (do resultado e do antes): o zoom arrastado além dele para nele.
  const zFim = useMemo(() => {
    let maximo = 0;
    for (const s of [r.col.z, antes?.col.z]) if (s) for (let i = 0; i < s.length; i++) if (s[i] > maximo) maximo = s[i];
    return maximo;
  }, [r, antes]);
  // A janela de profundidade da vertical inteira: da superfície à leitura mais funda, em metro inteiro.
  const z1 = Math.max(1, Math.ceil(zFim));
  // O trecho do eixo: o zoom (onda 347), ou a vertical inteira.
  const zoom = useMemo(() => zoomRecortado(p.zoom, [0, zFim]), [p.zoom, zFim]);
  const janela = useMemo((): [number, number] => (zoom ? [zoom.de, zoom.ate] : [0, z1]), [zoom, z1]);
  const ez = useMemo(() => escala('linear', janela, [CABECALHO, CABECALHO + altura]), [janela, altura]);
  const naJanela = (z: number): boolean => z >= janela[0] - 1e-9 && z <= janela[1] + 1e-9;

  // As linhas de trilhas: quantas cabem lado a lado, cada linha com o seu eixo.
  const comUnidades = (p.camadas?.length ?? 0) > 0;
  const estreita = largura > 0 && largura < 640;
  const larguraDasUnidades = comUnidades ? (estreita ? UNIDADES_ESTREITA : UNIDADES) : 0;
  const visiveis = useMemo(() => p.trilhas.map((def) => {
    const series = def.series.filter((s) => {
      const m = marcaDaColuna(s.coluna, r.premissas);
      return m.mostrar && (mostrarTudo || m.homologada);
    });
    const naoHomologadas = series
      .map((s) => ({ s, m: marcaDaColuna(s.coluna, r.premissas) }))
      .filter((x) => !x.m.homologada);
    // A cor da zona só com a coluna da zona homologada (a regra do PDF); sem ela, a nota diz por quê.
    const zonaDaCor = p.colorirPorZona && def.preencherPorZona ? def.preencherPorZona : null;
    const marcaDaZona = zonaDaCor ? marcaDaColuna(zonaDaCor as ColunaDoMotor, r.premissas) : null;
    return {
      def, series, homologada: naoHomologadas.length === 0,
      motivo: naoHomologadas.map((x) => `${GRANDEZAS[x.s.coluna].rotulo}: ${x.m.motivo}`).join(' · '),
      pinta: zonaDaCor && marcaDaZona?.homologada ? zonaDaCor : null,
      semCor: zonaDaCor && !marcaDaZona?.homologada ? (marcaDaZona?.motivo ?? 'fora das condições homologadas') : null,
    };
  }).filter((t) => t.series.length > 0), [p.trilhas, r, mostrarTudo, p.colorirPorZona]);

  const util = Math.max(LARGURA_MINIMA, largura - EIXO - larguraDasUnidades);
  const porLinha = Math.max(1, Math.min(visiveis.length, Math.floor(util / LARGURA_MINIMA)));
  const larguraDaTrilha = util / porLinha;
  const linhas = useMemo(() => {
    const saida: Coluna[][] = [];
    visiveis.forEach((t, k) => {
      const linha = Math.floor(k / porLinha);
      if (!saida[linha]) saida[linha] = [];
      const x0 = EIXO + (k % porLinha) * larguraDaTrilha;
      // No zoom, o automático sai só das leituras do trecho visível (§4.9); o domínio fixo da definição (o Ic) fica.
      const daSerie = (x: Resultado, coluna: Grandeza): ArrayLike<number> => (zoom ? valoresNoTrecho(x.col.z, x.col[coluna], janela) : x.col[coluna]);
      const valores: ArrayLike<number>[] = t.series.flatMap((s) => [daSerie(r, s.coluna), ...(antes ? [daSerie(antes, s.coluna)] : [])]);
      if (t.def.linha && Number.isFinite(t.def.linha.valor)) valores.push([t.def.linha.valor]);
      // A escala escolhida (onda 347): o tipo e os limites dela; no automático, o domínio de hoje no tipo escolhido.
      const ajuste = p.escalas?.[t.def.chave] ?? null;
      const tipo = ajuste?.tipo ?? t.def.escala;
      const fixos = limitesFixos(ajuste);
      const hoje = t.def.dominio && tipo === t.def.escala ? t.def.dominio : dominioDe(valores, tipo, { comZero: tipo === 'linear' });
      // No automático linear, o de hoje (com o zero quando a trilha o traz; o Ic de 1 a 4), mesmo com o passo digitado.
      const dominioX = ajuste && (fixos || tipo === 'log') ? dominioDaEscala(ajuste, hoje) : hoje;
      const direita = x0 + larguraDaTrilha - 6 - (t.def.faixaDeZonas ? FAIXA + 2 : 0);
      saida[linha].push({
        def: t.def, x0, largura: larguraDaTrilha, series: t.series, homologada: t.homologada, motivo: t.motivo,
        dominioX, ex: escala(tipo, dominioX, [x0 + 6, direita]), pinta: t.pinta, tipo, ajuste, fixos,
      });
    });
    return saida;
  }, [visiveis, porLinha, larguraDaTrilha, r, antes, p.escalas, zoom, janela]);

  const alturaDaLinha = CABECALHO + altura + RODAPE;
  const larguraTotal = EIXO + porLinha * larguraDaTrilha + larguraDasUnidades;

  // O traço de cada série (dizimado pelo pixel), do resultado e do antes — só muda com os dados e o tamanho.
  const tracos = useMemo(() => linhas.map((linha) => linha.map((c) => ({
    chave: c.def.chave,
    depois: c.series.map((s) => {
      const d = dizimarPorPixel(r.col.z, r.col[s.coluna], { z0: janela[0], z1: janela[1], altura });
      return { classe: s.classe, d: caminho(d.z, d.v, d.quebras, c.ex, ez, c.fixos) };
    }),
    antes: antes ? c.series.map((s) => {
      const d = dizimarPorPixel(antes.col.z, antes.col[s.coluna], { z0: janela[0], z1: janela[1], altura });
      return caminho(d.z, d.v, d.quebras, c.ex, ez, c.fixos);
    }) : [],
    corte: c.fixos ? corteDaTrilha(r, c.series, c.fixos.max, janela) : null,
    faixa: c.def.faixaDeZonas ? faixaDeZonas(r, c.def.faixaDeZonas, ez) : [],
    preenchimento: c.pinta ? poligonosPorZona({
      r, coluna: c.series.some((s) => s.coluna === 'qt') ? 'qt' : c.series[c.series.length - 1].coluna,
      zonas: c.pinta === 'sbt' ? r.sbt : r.sbtn, ex: c.ex, ez, dominio: c.dominioX, tipo: c.tipo,
      xBase: c.ex.para(c.tipo === 'log' ? c.dominioX[0] : Math.min(Math.max(0, c.dominioX[0]), c.dominioX[1])),
      janela: { z0: janela[0], z1: janela[1], altura }, rotulo: c.pinta === 'sbt' ? 'SBT' : 'SBTn',
    }) : [],
    fundo: c.def.fundoDeIc ? FAIXAS_DE_IC.map((f) => {
      const zona = (ZONAS_SBTN as Readonly<Record<number, { token: string; nome: string }>>)[f.zona];
      const a = c.ex.para(Math.max(f.de, c.dominioX[0]));
      const b = c.ex.para(Math.min(f.ate, c.dominioX[1]));
      return { x: Math.min(a, b), w: Math.abs(b - a), token: zona.token, texto: `SBTn ${f.zona} · ${zona.nome} (Ic ${faixaDoIc(f)})` };
    }).filter((f) => Number.isFinite(f.x) && f.w > 0) : [],
  }))), [linhas, r, antes, janela, altura, ez]);

  // O arrasto da seleção e do zoom (a prévia, antes de soltar; `zoom`: a faixa tracejada com o rótulo «5,00 → 9,00 m»).
  const [previa, setPrevia] = useState<{ de: number; ate: number; zoom: boolean } | null>(null);
  const comZoom = !!p.aoZoom;
  const zDoPonteiro = (e: PointerEvent<SVGRectElement>): number => {
    const caixa = (e.currentTarget.ownerSVGElement ?? e.currentTarget).getBoundingClientRect();
    const z = ez.de(e.clientY - caixa.top);
    return Math.min(janela[1], Math.max(janela[0], z));
  };
  /** A profundidade da leitura mais próxima (o cursor anda nas leituras). */
  const naLeitura = (z: number): number => {
    const i = indiceNaProfundidade(r.col.z, z);
    return i < 0 ? z : r.col.z[i];
  };
  // Sem o zoom, o gesto de hoje: arrastar em qualquer sentido seleciona. Com ele: para baixo, a seleção e o zoom (o
  // trecho menor que o mínimo só seleciona); para cima, a vertical inteira e a seleção limpa.
  const gesto = useArrastoVertical<SVGRectElement>({
    caixa: ref,
    valor: zDoPonteiro,
    minimo: comZoom ? ARRASTO_DO_ZOOM : ARRASTO_MINIMO,
    toqueLongo: comZoom,
    prenderAoApertar: true,
    aoPassar: (z) => p.aoCursor(naLeitura(z)),
    aoClicar: (z) => p.aoCursor(naLeitura(z)),
    aoPrevia: (a) => {
      if (!a) { setPrevia(null); return; }
      if (comZoom && !a.desce && a.fim !== a.inicio) { setPrevia(null); return; }
      setPrevia({ de: Math.min(a.inicio, a.fim), ate: Math.max(a.inicio, a.fim), zoom: comZoom });
    },
    aoSoltar: (a) => {
      const trecho = { de: Math.min(a.inicio, a.fim), ate: Math.max(a.inicio, a.fim) };
      if (!comZoom) { p.aoSelecionar(trecho); return; }
      if (!a.desce) {
        p.aoZoom?.(null);
        p.aoSelecionar(null);
        return;
      }
      p.aoSelecionar(trecho);
      const z = zoomRecortado(trecho, [0, zFim]);
      if (z) p.aoZoom?.(z);
    },
  });

  const aoTeclar = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.target !== e.currentTarget) return; // um link de dissipação com o foco segue o dele
    if (e.key === 'Escape') {
      p.aoSelecionar(null);
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const zs = r.col.z;
    if (zs.length === 0) return;
    const atual = p.cursor === null ? -1 : indiceNaProfundidade(zs, p.cursor);
    const i = atual < 0 ? 0 : Math.min(zs.length - 1, Math.max(0, atual + (e.key === 'ArrowDown' ? 1 : -1)));
    const z = zs[i];
    if (e.shiftKey) {
      const ancora = p.selecao
        ? (p.cursor !== null && Math.abs(p.cursor - p.selecao.de) < 1e-9 ? p.selecao.ate : p.selecao.de)
        : (p.cursor ?? z);
      p.aoSelecionar({ de: Math.min(ancora, z), ate: Math.max(ancora, z) });
    }
    p.aoCursor(z);
  };

  // As marcas do eixo: profundidades redondas, ou — por cota — cotas redondas (z = cota da boca − cota).
  const quantasMarcas = Math.max(4, Math.round(altura / 60));
  const boca = porCota ? (p.cota as number) : 0;
  const cotas = porCota ? marcas('linear', [boca - janela[1], boca - janela[0]], quantasMarcas) : [];
  const zMarca = porCota ? cotas.map((c) => boca - c) : marcas('linear', janela, quantasMarcas);
  const textoDaProfundidade = (z: number): string =>
    porCota ? rotuloDaMarca(boca - z, cotas, 'linear') : rotuloDaMarca(z, zMarca, 'linear');
  // A seleção (ou a prévia) recortada no trecho à vista.
  const selecaoInteira = previa ?? p.selecao;
  const selecaoNaJanela = selecaoInteira && selecaoInteira.ate >= janela[0] && selecaoInteira.de <= janela[1]
    ? { de: Math.max(janela[0], selecaoInteira.de), ate: Math.min(janela[1], selecaoInteira.ate) } : null;
  // No zoom, a seleção que cobre o trecho inteiro (a do próprio arrasto) não pinta a janela de laranja (decisão R18): o
  // «Trecho …» da tela e o destaque nos ábacos continuam. A que é menor (Shift + setas) pinta, como hoje.
  const cobreAJanela = !!zoom && !previa && !!selecaoNaJanela
    && ez.para(selecaoNaJanela.de) - CABECALHO <= 2 && CABECALHO + altura - ez.para(selecaoNaJanela.ate) <= 2;
  const selecao = cobreAJanela ? null : selecaoNaJanela;
  const destacados = useMemo(() => marcasDosDestacados(r, p.destacados ?? null, ez, janela), [r, p.destacados, ez, janela]);
  const naUsada = p.na !== undefined && p.na !== null && Number.isFinite(p.na) ? p.na : null;
  const comAgua = (c: Coluna): boolean => c.series.some((s) => s.coluna === 'u2' || s.coluna === 'u0');
  // O nome de cada unidade na coluna, quebrado em linhas; o que a coluna não escreve inteiro (uma palavra maior que
  // ela, ou a camada fina demais para as linhas) vai escrito numa lista embaixo: o `title` nunca é o único caminho
  // (P-UI-44). Sem caber, a coluna ainda escreve o código da unidade («U2» de «U2 · argila mole») se ele couber.
  const caracteres = Math.max(1, Math.floor((larguraDasUnidades - 16) / LETRA));
  const nomesNaColuna = (p.camadas ?? []).map((c) => {
    const altura = ez.para(c.base) - ez.para(c.topo);
    const linhas = quebrarONome(c.unidade, caracteres);
    if (linhas && linhas.length > 0 && linhas.length * LINHA_DO_NOME + 4 <= altura) return { linhas, inteiro: true };
    const codigo = c.unidade.split(' · ')[0].trim();
    return { linhas: codigo.length <= caracteres && altura >= 16 ? [codigo] : [], inteiro: false };
  });
  const unidadesNaLista = (p.camadas ?? []).filter((_, k) => !nomesNaColuna[k].inteiro);
  // A legenda das dissipações com os valores, escrita (a forma e a cor nunca sozinhas), só com a trilha de u₂ à vista.
  const comValores = (p.dissipacoes ?? []).filter((d) => [d.inicio, d.fim, d.ui].some((v) => v !== undefined && Number.isFinite(v)));
  const legendaDasDissipacoes = comValores.length > 0 && linhas.flat().some(comAgua)
    ? `${comValores.length === 1 ? 'Dissipação' : 'Dissipações'} a ${comValores.map((d) => d.rotulo).join(' · ')}, na escala de u₂: `
      + '● início (o primeiro u lido) · ▼ fim (o u₀ adotado) · ◆ uᵢ em t = 0 (da reta)'
      + (comValores.some((d) => d.extrapolado) ? ' · «ext.»: o t₅₀ extrapolado pela reta (a curva não chegou a u₅₀)' : '')
      + (comValores.some((d) => d.para) ? ' — o link abre a dissipação.' : '.')
    : null;
  // A legenda das zonas de cada faixa que aparece (a cor sempre com o número e o nome).
  const faixas = [...new Set(linhas.flat().map((c) => c.def.faixaDeZonas).filter((f): f is 'sbtn' | 'sbtnMod' => !!f))];

  return (
    <div
      ref={ref}
      className="cptu-trilhas"
      tabIndex={0}
      role="group"
      aria-label={`Trilhas da vertical por ${porCota ? 'cota' : 'profundidade'}: setas sobem e descem uma leitura, Shift estende a seleção, Esc limpa`}
      onKeyDown={aoTeclar}
    >
      {p.aoZoom ? (
        <BarraDoZoom zoom={zoom} limites={[0, zFim]} rotulo="Profundidade" aoZoom={(z) => p.aoZoom?.(z)} />
      ) : null}
      {p.eixo === 'cota' && !porCota ? (
        <p className="cptu-nota">A vertical não tem cota: o eixo fica por profundidade.</p>
      ) : null}
      {largura > 0 ? linhas.map((linha, n) => (
        <div key={n} className="cptu-linha-de-trilhas">
        <svg
          className="cptu-trilhas-linha"
          width={larguraTotal}
          height={alturaDaLinha}
          data-de={janela[0]}
          data-ate={janela[1]}
          role="img"
          aria-label={linha.map((c) => c.def.titulo).join(', ')}
        >
          {/* O eixo de profundidade (ou de cota). */}
          <text className="cptu-texto cptu-texto-2" x={EIXO - 6} y={CABECALHO - 8} textAnchor="end">
            {porCota ? 'cota (m)' : 'z (m)'}
          </text>
          {zMarca.map((z) => (
            <g key={`z${z}`}>
              <line className="cptu-grade" x1={EIXO} x2={EIXO + linha.length * larguraDaTrilha} y1={ez.para(z)} y2={ez.para(z)} />
              <text className="cptu-texto cptu-texto-2" x={EIXO - 6} y={ez.para(z) + 4} textAnchor="end">
                {textoDaProfundidade(z)}
              </text>
            </g>
          ))}

          {linha.map((c, k) => {
            const traco = tracos[n][k];
            const marcasX = marcasQueCabem(c.tipo, c.dominioX, c.ex, c.ajuste);
            return (
              <g key={c.def.chave} className="cptu-trilha">
                <rect className="cptu-moldura" x={c.x0 + 2} y={CABECALHO} width={c.largura - 4} height={altura} />
                {marcasX.map((v) => (
                  <g key={`x${v}`}>
                    <line className="cptu-grade" x1={c.ex.para(v)} x2={c.ex.para(v)} y1={CABECALHO} y2={CABECALHO + altura} />
                    <text className="cptu-texto cptu-texto-2" x={c.ex.para(v)} y={CABECALHO - 6} textAnchor="middle">
                      {rotuloDaMarca(v, marcasX, c.tipo)}
                    </text>
                  </g>
                ))}
                <text className="cptu-texto cptu-titulo" x={c.x0 + 6} y={16}>
                  {c.def.titulo}{c.def.unidade ? ` (${c.def.unidade})` : ''}
                </text>
                <LegendaDasSeries series={c.series} x={c.x0 + 6} y={22} />
                {!c.homologada ? (
                  <text className="cptu-texto cptu-marca" x={c.x0 + 6} y={52}>
                    <title>{c.motivo}</title>
                    {MARCA_NAO_HOMOLOGADA}
                  </text>
                ) : null}
                <svg x={c.x0 + 2} y={CABECALHO} width={c.largura - 4} height={altura} overflow="hidden">
                  <g transform={`translate(${-(c.x0 + 2)} ${-CABECALHO})`}>
                    {traco.fundo.map((f) => (
                      <rect key={`i${f.x}`} className="cptu-fundo-de-ic" x={f.x} y={CABECALHO} width={f.w} height={altura}
                        fill={`var(${f.token})`}>
                        <title>{f.texto}</title>
                      </rect>
                    ))}
                    {traco.preenchimento.map((f, j) => (
                      <path key={`p${j}`} className="cptu-zona-preenchida" d={f.d} fill={`var(${f.token})`}>
                        <title>{f.texto}</title>
                      </path>
                    ))}
                    {traco.faixa.map((f) => (
                      <rect key={`${f.y}`} x={c.x0 + c.largura - 4 - FAIXA} y={f.y} width={FAIXA} height={f.h} fill={`var(${f.token})`}>
                        <title>{f.texto}</title>
                      </rect>
                    ))}
                    {traco.antes.map((d, j) => <path key={`a${j}`} className="cptu-antes-serie" d={d} />)}
                    {traco.depois.map((s, j) => <path key={`d${j}`} className={`cptu-serie ${s.classe}`} d={s.d} />)}
                    {naUsada !== null && comAgua(c) ? (
                      <line className="cptu-na" x1={c.x0 + 2} x2={c.x0 + c.largura - 2} y1={ez.para(naUsada)} y2={ez.para(naUsada)} />
                    ) : null}
                    {c.def.linha && Number.isFinite(c.def.linha.valor) ? (
                      <line className="cptu-linha-de-referencia" x1={c.ex.para(c.def.linha.valor)} x2={c.ex.para(c.def.linha.valor)}
                        y1={CABECALHO} y2={CABECALHO + altura} />
                    ) : null}
                  </g>
                </svg>
                {traco.corte ? (
                  // O valor passou do máximo fixo: «▸» na borda direita, na primeira cota em que passa — ESCOLHA DA TELA,
                  // SEM FONTE [esc.corte].
                  <text className="cptu-texto cptu-corte" data-z={traco.corte.z} x={c.ex.para(c.fixos?.max ?? NaN) - 1}
                    y={ez.para(traco.corte.z) + 4} textAnchor="end">
                    <title>{`${tituloDe(c.def)}: passa de ${numeroBr(c.fixos?.max ?? NaN, casasDoLimite(c.fixos?.max ?? NaN))} a partir de ${numeroBr(traco.corte.z, 2)} m — cortado na borda`}</title>
                    ▸
                  </text>
                ) : null}
                {c.def.linha && Number.isFinite(c.def.linha.valor) ? (
                  <text className="cptu-texto cptu-rotulo-da-linha" x={c.ex.para(c.def.linha.valor) + 3} y={CABECALHO + 14}>
                    {c.def.linha.rotulo}
                  </text>
                ) : null}
                {comAgua(c) ? (p.dissipacoes ?? []).filter((d) => naJanela(d.z)).map((d) => {
                  const y = ez.para(d.z);
                  // Com os valores, as três marcas na escala de pressão de u₂ (o valor fora da escala vai à borda).
                  const naEscala = (v: number | undefined): number => {
                    if (v === undefined || !Number.isFinite(v)) return NaN;
                    return c.ex.para(Math.min(c.dominioX[1], Math.max(c.dominioX[0], v)));
                  };
                  const xi = naEscala(d.inicio);
                  const xf = naEscala(d.fim);
                  const xu = naEscala(d.ui);
                  const comValores = Number.isFinite(xi) || Number.isFinite(xf) || Number.isFinite(xu);
                  if (!comValores) {
                    return (
                      <g key={`d${d.z}`} className="cptu-dissipacao-marca">
                        <circle cx={c.x0 + c.largura - 12} cy={y} r={4} />
                        {d.para ? null : (
                          <text className="cptu-texto" x={c.x0 + c.largura - 20} y={y + 4} textAnchor="end">{d.rotulo}</text>
                        )}
                      </g>
                    );
                  }
                  return (
                    <g key={`d${d.z}`} className="cptu-dissipacao-marca com-valores" data-z={d.z}>
                      {Number.isFinite(xu) ? (
                        <path className="cptu-dissipacao-ui" data-u={d.ui} d={`M${xu} ${y - 6}l5 6l-5 6l-5 -6z`}>
                          <title>{`${d.rotulo}: ◆ uᵢ em t = 0 ${numeroBr(d.ui as number, 2)} kPa`}</title>
                        </path>
                      ) : null}
                      {Number.isFinite(xi) ? (
                        <circle data-u={d.inicio} cx={xi} cy={y} r={4}>
                          <title>{`${d.rotulo}: ● início ${numeroBr(d.inicio as number, 2)} kPa`}</title>
                        </circle>
                      ) : null}
                      {Number.isFinite(xf) ? (
                        <path className="cptu-dissipacao-fim" data-u={d.fim} d={`M${xf - 5} ${y - 5}h10l-5 9z`}>
                          <title>{`${d.rotulo}: ▼ fim (u₀ adotado) ${numeroBr(d.fim as number, 2)} kPa`}</title>
                        </path>
                      ) : null}
                      {d.extrapolado && Number.isFinite(xf) ? (
                        <text className="cptu-texto cptu-ext" x={xf + 7} y={y + 4}>ext.</text>
                      ) : null}
                      {d.para ? null : (
                        <text className="cptu-texto" x={c.x0 + c.largura - 20} y={y - 6} textAnchor="end">{d.rotulo}</text>
                      )}
                    </g>
                  );
                }) : null}
              </g>
            );
          })}

          {/* O NA no eixo. */}
          {naUsada !== null && naJanela(naUsada) ? (
            <g className="cptu-na-marca">
              <path d={`M${EIXO - 14} ${ez.para(naUsada) - 7}h10l-5 7z`} />
              <text className="cptu-texto cptu-texto-2" x={EIXO - 16} y={ez.para(naUsada) + 4} textAnchor="end">NA</text>
            </g>
          ) : null}

          {/* As camadas, as sugestões e os destacados do ábaco. */}
          {(p.camadas ?? []).flatMap((c) => [c.topo, c.base]).filter(naJanela).map((z, k) => (
            <line key={`c${k}`} className="cptu-camada" x1={EIXO} x2={EIXO + linha.length * larguraDaTrilha} y1={ez.para(z)} y2={ez.para(z)} />
          ))}
          {(p.sugestoes ?? []).filter(naJanela).map((z) => (
            <line key={`s${z}`} className="cptu-sugestao" x1={EIXO} x2={EIXO + linha.length * larguraDaTrilha} y1={ez.para(z)} y2={ez.para(z)} />
          ))}
          {destacados ? <path className="cptu-destacado" d={destacados} /> : null}
          {comUnidades ? (
            <g className="cptu-unidades">
              <text className="cptu-texto cptu-titulo" x={EIXO + porLinha * larguraDaTrilha + 6} y={16}>{estreita ? 'Unid.' : 'Unidades'}</text>
              <svg x={EIXO + porLinha * larguraDaTrilha + 4} y={CABECALHO} width={larguraDasUnidades - 8} height={altura} overflow="hidden">
                <g transform={`translate(0 ${-CABECALHO})`}>
                  {(p.camadas ?? []).map((c, k) => {
                    const y0 = ez.para(c.topo);
                    const y1 = ez.para(c.base);
                    const { linhas: nome } = nomesNaColuna[k];
                    const primeira = (y0 + y1) / 2 + 4 - ((nome.length - 1) * LINHA_DO_NOME) / 2;
                    return (
                      <g key={`u${c.topo}`}>
                        <rect className="cptu-unidade" x={0} y={y0} width={larguraDasUnidades - 8} height={Math.max(0, y1 - y0)}>
                          <title>{`${c.unidade}: ${faixaBr(c.topo, c.base)} m`}</title>
                        </rect>
                        {nome.length > 0 ? (
                          <text className="cptu-texto cptu-nome-da-unidade" x={4} y={primeira}>
                            {nome.map((l, j) => <tspan key={j} x={4} dy={j === 0 ? 0 : LINHA_DO_NOME}>{l}</tspan>)}
                          </text>
                        ) : null}
                      </g>
                    );
                  })}
                </g>
              </svg>
            </g>
          ) : null}

          {/* A seleção e o cursor, comuns a todas as trilhas. */}
          {selecao ? (
            <rect
              className={`cptu-selecao${previa?.zoom ? ' cptu-previa-do-zoom' : ''}`}
              x={EIXO}
              width={linha.length * larguraDaTrilha}
              y={ez.para(selecao.de)}
              height={Math.max(1, ez.para(selecao.ate) - ez.para(selecao.de))}
            />
          ) : null}
          {previa?.zoom && selecao ? (
            <text className="cptu-texto cptu-previa-do-zoom-texto" x={EIXO + 4} y={ez.para(selecao.de) - 4}>
              {`${numeroBr(previa.de, 2)} → ${numeroBr(Math.min(previa.ate, zFim), 2)} m`}
            </text>
          ) : null}
          {p.cursor !== null && Number.isFinite(p.cursor) && naJanela(p.cursor) ? (
            <g>
              <line className="cptu-cursor" x1={EIXO} x2={EIXO + linha.length * larguraDaTrilha} y1={ez.para(p.cursor)} y2={ez.para(p.cursor)} />
              <text className="cptu-texto cptu-cursor-texto" x={EIXO + linha.length * larguraDaTrilha - 4} y={ez.para(p.cursor) - 4} textAnchor="end">
                {porCota ? `cota ${numeroBr(boca - p.cursor, 2)} m` : `${numeroBr(p.cursor, 2)} m`}
              </text>
            </g>
          ) : null}

          {/* A captura do ponteiro: cursor ao passar, seleção ao arrastar, cursor ao tocar. */}
          <rect
            className="cptu-captura"
            x={EIXO}
            y={CABECALHO}
            width={linha.length * larguraDaTrilha}
            height={altura}
            onPointerDown={gesto.onPointerDown}
            onPointerMove={gesto.onPointerMove}
            onPointerUp={gesto.onPointerUp}
            onPointerCancel={() => gesto.onPointerCancel()}
          />
        </svg>
        {/* O botão «Escala» de cada trilha (onda 347): no título, fora do desenho (que é uma imagem para o leitor). */}
        {p.aoMudarEscala ? linha.map((c) => (
          <div key={`escala-${c.def.chave}`} className="cptu-escala-na-trilha" style={{ left: c.x0 + c.largura - 4, top: 0 }}>
            <ControleDaEscala grandeza={c.def.chave} rotulo={c.def.titulo} unidade={c.def.unidade} escala={c.ajuste}
              dominio={c.dominioX} aoMudar={(e) => p.aoMudarEscala?.(c.def.chave, e)} />
          </div>
        )) : null}
        {/* Os links das dissipações: fora do desenho (que é uma imagem para o leitor de tela), alcançáveis pelo
            teclado, com alvo de 44 px no celular. */}
        {linha.filter(comAgua).flatMap((c) => (p.dissipacoes ?? []).filter((d) => d.para && naJanela(d.z)).map((d) => (
          <a
            key={`${c.def.chave}${d.z}`}
            className="cptu-link-dissipacao"
            href={d.para}
            // Com as três marcas na linha da dissipação, o link vai logo acima delas (não as cobre).
            style={{ left: c.x0 + c.largura - 18, top: ez.para(d.z) - ([d.inicio, d.fim, d.ui].some((x) => x !== undefined && Number.isFinite(x)) ? 16 : 0) }}
            onClick={(e) => {
              if (!p.aoNavegar || !d.para) return;
              e.preventDefault();
              p.aoNavegar(d.para);
            }}
          >
            <span>{d.rotulo}</span>
          </a>
        )))}
        </div>
      )) : null}
      {largura > 0 ? linhas.flat().filter((c) => !c.homologada).map((c) => (
        <p key={c.def.chave} className="cptu-nota">
          <span className="cptu-marca">{MARCA_NAO_HOMOLOGADA}</span> · {c.motivo}
        </p>
      )) : null}
      {largura > 0 && legendaDasDissipacoes ? <p className="cptu-nota cptu-legenda-dissipacoes">{legendaDasDissipacoes}</p> : null}
      {largura > 0 ? visiveis.filter((t) => t.semCor).map((t) => (
        <p key={`sem-cor-${t.def.chave}`} className="cptu-nota">
          {tituloDe(t.def)} sem a {CLASSIFICACAO_DA_COR[t.def.preencherPorZona as 'sbt' | 'sbtn']}:{' '}
          <span className="cptu-marca">{MARCA_NAO_HOMOLOGADA}</span> · {t.semCor}
        </p>
      )) : null}
      {largura > 0 ? linhas.flat().filter((c) => c.pinta).map((c) => (
        <LegendaSbt key={`cor-${c.def.chave}`} tipo="sbtn" soPresentes
          presentes={new Set(Array.from((c.pinta === 'sbt' ? r.sbt : r.sbtn).subarray(0, r.n)).filter((z) => z > 0))}
          titulo={`${tituloDe(c.def)} — ${CLASSIFICACAO_DA_COR[c.pinta as 'sbt' | 'sbtn']}`} />
      )) : null}
      {largura > 0 ? linhas.flat().filter((c) => c.def.fundoDeIc).map((c) => (
        <p key={`fundo-${c.def.chave}`} className="cptu-nota cptu-legenda-fundo">
          {tituloDe(c.def)} — fundo: a zona SBTn pelas fronteiras de Ic (1,31 · 2,05 · 2,60 · 2,95 · 3,60):{' '}
          {FAIXAS_DE_IC.map((f) => `${f.zona} ${(ZONAS_SBTN as Readonly<Record<number, { nome: string }>>)[f.zona].nome} (${faixaDoIc(f)})`).join(' · ')}.
        </p>
      )) : null}
      {largura > 0 ? linhas.flat().filter((c) => c.def.linha).map((c) => (
        <p key={`linha-${c.def.chave}`} className="cptu-nota">{c.def.linha?.nota}</p>
      )) : null}
      {largura > 0 && unidadesNaLista.length > 0 ? (
        <ul className="cptu-lista-de-unidades" aria-label="Unidades que o desenho não escreve inteiras">
          {unidadesNaLista.map((c) => (
            <li key={`${c.topo}`}><b>{c.unidade}</b> · {faixaBr(c.topo, c.base)} m</li>
          ))}
        </ul>
      ) : null}
      {largura > 0 ? faixas.map((f) => <LegendaSbt key={f} tipo={f} presentes={zonasPresentes(r, f)} />) : null}
    </div>
  );
}

/** As casas de um limite escrito: as que ele tem, até três. */
const casasDoLimite = (v: number): number => Math.min(3, (String(v).split('.')[1] ?? '').length);

/**
 * O corte de uma trilha com o máximo fixo: a primeira profundidade do trecho à vista em que alguma série passa dele
 * (null: nenhuma passa). As leituras todas, nunca os pontos do traço.
 */
function corteDaTrilha(
  r: Resultado, series: readonly { coluna: Grandeza }[], maximo: number, janela: readonly [number, number],
): { z: number } | null {
  let melhor = Infinity;
  for (const s of series) {
    const v = r.col[s.coluna];
    const z = r.col.z;
    const n = Math.min(v.length, z.length);
    for (let i = 0; i < n; i++) {
      if (!(z[i] >= janela[0] - 1e-9 && z[i] <= janela[1] + 1e-9)) continue; // no zoom, só o trecho à vista
      if (Number.isFinite(v[i]) && v[i] > maximo && Number.isFinite(z[i])) { if (z[i] < melhor) melhor = z[i]; break; }
    }
  }
  return Number.isFinite(melhor) ? { z: melhor } : null;
}

/** «qt (MPa)», ou só «Ic» sem unidade. */
const tituloDe = (d: DefinicaoDaTrilha): string => (d.unidade ? `${d.titulo} (${d.unidade})` : d.titulo);

/** A faixa de Ic escrita: «< 1,31», «1,31–2,05», «> 3,60». */
function faixaDoIc(f: { de: number; ate: number }): string {
  if (f.de <= 0) return `Ic < ${numeroBr(f.ate, 2)}`;
  if (!Number.isFinite(f.ate)) return `Ic > ${numeroBr(f.de, 2)}`;
  return `Ic ${numeroBr(f.de, 2)}–${numeroBr(f.ate, 2)}`;
}

/** As zonas que a vertical tem (para a legenda dizer quais não ocorrem). */
function zonasPresentes(r: Resultado, tipo: 'sbtn' | 'sbtnMod'): Set<number> {
  return new Set(Array.from(tipo === 'sbtn' ? r.sbtn : r.sbtnMod).filter((z) => z > 0));
}

/** A legenda em caixa das séries de uma trilha: o traço de cada uma e o nome (a cor nunca vai sozinha). */
function LegendaDasSeries({ series, x, y }: { series: readonly { coluna: Grandeza; classe: string }[]; x: number; y: number }): JSX.Element {
  let cursor = 4;
  const itens = series.map((s) => {
    const rotulo = GRANDEZAS[s.coluna].rotulo;
    const item = { s, rotulo, x: cursor };
    cursor += 20 + rotulo.length * 7 + 6;
    return item;
  });
  return (
    <g className="cptu-legenda-das-series" transform={`translate(${x} ${y})`}>
      <rect className="cptu-caixa-da-legenda" x={0} y={0} width={cursor - 2} height={15} />
      {itens.map((i) => (
        <g key={i.s.coluna} transform={`translate(${i.x} 0)`}>
          <line className={`cptu-serie ${i.s.classe}`} x1={0} x2={16} y1={7.5} y2={7.5} />
          <text className="cptu-texto" x={20} y={11.5}>{i.rotulo}</text>
        </g>
      ))}
    </g>
  );
}

/** A faixa de zonas: um retângulo por trecho de leituras seguidas da mesma zona (as leituras todas). */
function faixaDeZonas(r: Resultado, tipo: 'sbtn' | 'sbtnMod', ez: Escala): { y: number; h: number; token: string; texto: string }[] {
  const zonas = tipo === 'sbtn' ? r.sbtn : r.sbtnMod;
  const z = r.col.z;
  const n = Math.min(zonas.length, z.length);
  const saida: { y: number; h: number; token: string; texto: string }[] = [];
  const zonasDoTipo = (tipo === 'sbtn' ? ZONAS_SBTN : ZONAS_SBTN_MOD) as Readonly<Record<number, { token: string; nome: string }>>;
  let i = 0;
  while (i < n) {
    let j = i;
    while (j + 1 < n && zonas[j + 1] === zonas[i]) j++;
    const zona = zonasDoTipo[zonas[i]];
    if (zona) {
      const topo = i === 0 ? z[0] : (z[i - 1] + z[i]) / 2;
      const base = j === n - 1 ? z[n - 1] : (z[j] + z[j + 1]) / 2;
      const y = ez.para(topo);
      saida.push({ y, h: Math.max(0.5, ez.para(base) - y), token: zona.token, texto: `${tipo === 'sbtn' ? 'SBTn' : 'SBTn modificado'} ${zonas[i]} · ${zona.nome}` });
    }
    i = j + 1;
  }
  return saida;
}

/** As marcas no eixo das leituras destacadas pelo ábaco: um traço por linha de pixel que tem alguma. */
function marcasDosDestacados(r: Resultado, destacados: Uint8Array | null, ez: Escala, janela: readonly [number, number]): string | null {
  if (!destacados) return null;
  const linhas = new Set<number>();
  const n = Math.min(destacados.length, r.col.z.length);
  for (let i = 0; i < n; i++) {
    const z = r.col.z[i];
    if (destacados[i] && z >= janela[0] - 1e-9 && z <= janela[1] + 1e-9) linhas.add(Math.round(ez.para(z)));
  }
  if (linhas.size === 0) return null;
  return [...linhas].map((y) => `M${EIXO - 8} ${y}h8`).join('');
}
